import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { scrollToId, site } from "../config/site";

export default function About() {
  return (
    <section id="sobre-mi" data-testid="about-section" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Reveal>
              <div className="relative max-w-md mx-auto lg:mx-0">
                <div
                  aria-hidden="true"
                  className="absolute -bottom-5 -right-5 w-full h-full rounded-[2rem] bg-sage-light"
                />
                <div className="relative overflow-hidden rounded-[2rem] shadow-[0_20px_50px_rgba(27,59,111,0.12)] aspect-[4/5]">
                  <img
                    src="/uploads/lidia-retrato.webp"
                    alt="Lidia Montiel en su mesa de trabajo del gabinete ATENTO, sonriendo"
                    data-testid="about-portrait"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <motion.div
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.3 }}
                  className="absolute -bottom-6 left-6 bg-atento-blue text-ivory rounded-2xl px-6 py-4 shadow-xl text-center"
                  data-testid="about-name-card"
                >
                  <p className="font-serif text-xl font-semibold leading-tight">{site.owner}</p>
                  <p className="text-[11px] font-accent font-medium uppercase tracking-[0.16em] text-sage mt-1">
                    Pedagoga · Orientadora educativa
                  </p>
                </motion.div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <SectionHeading
              eyebrow="Sobre mí"
              title="Una mirada individualizada para cada persona"
            />
            <Reveal delay={0.15} className="mt-7 space-y-5 text-base sm:text-lg leading-relaxed text-charcoal/75">
              <p data-testid="about-text-intro">
                Soy <strong className="text-atento-blue font-semibold">Lidia Montiel</strong>,
                pedagoga y orientadora educativa. Mi trabajo consiste en comprender cómo
                aprende cada persona, identificar sus necesidades y ofrecer herramientas
                que le permitan avanzar académica y personalmente.
              </p>
              <p>
                En ATENTO cada intervención parte de una mirada individualizada. No se trata
                únicamente de mejorar los resultados escolares, sino de comprender las
                dificultades, potenciar las capacidades y ayudar a cada niño, niña o
                adolescente a ganar autonomía y confianza.
              </p>
              <p>
                Trabajo junto a las familias para construir estrategias adaptadas a las
                necesidades de cada niño, niña o adolescente.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <button
                onClick={() => scrollToId("contacto")}
                data-testid="about-cta-button"
                className="mt-9 inline-flex items-center gap-2 font-accent font-semibold text-atento-blue border-b-2 border-sage hover:border-atento-blue pb-1 transition-colors duration-200 cursor-pointer"
              >
                Hablemos de vuestra situación
                <ArrowRight className="w-4 h-4" />
              </button>
            </Reveal>
          </div>
        </div>

        <div className="mt-24" data-testid="gallery-section">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <div>
              <p className="eyebrow">El gabinete</p>
              <h3 className="mt-3 font-serif text-2xl sm:text-3xl font-semibold text-atento-blue tracking-tight leading-snug">
                Un espacio pensado para la calma y la concentración
              </h3>
            </div>
            <p className="max-w-xs text-sm leading-relaxed text-charcoal/60">
              Luz natural, materiales cálidos y un ambiente acogedor en Chiclana de la Frontera.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                src: "/uploads/gabinete-1.webp",
                alt: "Mesa de trabajo del gabinete ATENTO con luz natural y estantería de madera",
              },
              {
                src: "/uploads/gabinete-2.webp",
                alt: "Lámina con el logotipo de ATENTO junto a un jarrón con eucalipto",
              },
              {
                src: "/uploads/gabinete-3.webp",
                alt: "Rincón de trabajo del gabinete con portátil, cuaderno y plantas",
              },
            ].map((g, i) => (
              <Reveal key={g.src} delay={i * 0.1} className={i === 0 ? "col-span-2 lg:col-span-1" : ""}>
                <div className="group overflow-hidden rounded-[1.5rem] aspect-[3/4] shadow-[0_10px_34px_rgba(27,59,111,0.08)]">
                  <img
                    src={g.src}
                    alt={g.alt}
                    loading="lazy"
                    data-testid={`gallery-image-${i}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
